/**
 * OmniFlow Motion — shared Framer Motion variants & transitions.
 * Import these instead of writing one-off motion configs per component.
 */
import type { Variants } from 'framer-motion';

// ─── Transitions ────────────────────────────────────────────────────────────

export const springFast = { type: 'spring', stiffness: 400, damping: 30 } as const;
export const springSmooth = { type: 'spring', stiffness: 300, damping: 28 } as const;
export const easeFade = { duration: 0.15, ease: [0.4, 0, 0.2, 1] } as const;
export const easeSlide = { duration: 0.2, ease: [0.4, 0, 0.2, 1] } as const;

// ─── Fade ────────────────────────────────────────────────────────────────────

/** Simple opacity fade — use on page sections, panels */
export const fadeIn: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: easeFade },
  exit: { opacity: 0, transition: { duration: 0.1 } },
};

// ─── Fade + slide up (most-used) ─────────────────────────────────────────────

/** Appears from slightly below — use on cards, list items, dialogs */
export const fadeUp: Variants = {
  hidden: { opacity: 0, y: 6 },
  visible: { opacity: 1, y: 0, transition: easeSlide },
  exit: { opacity: 0, y: 4, transition: { duration: 0.1 } },
};

/** Appears from slightly above — use on dropdowns, tooltips */
export const fadeDown: Variants = {
  hidden: { opacity: 0, y: -6 },
  visible: { opacity: 1, y: 0, transition: easeSlide },
  exit: { opacity: 0, y: -4, transition: { duration: 0.1 } },
};

// ─── Scale ───────────────────────────────────────────────────────────────────

/** Subtle scale-in — use on modals, popovers, command palette */
export const scaleIn: Variants = {
  hidden: { opacity: 0, scale: 0.97 },
  visible: { opacity: 1, scale: 1, transition: { ...springFast, opacity: { duration: 0.12 } } },
  exit: { opacity: 0, scale: 0.97, transition: { duration: 0.1 } },
};

// ─── Slide from side ─────────────────────────────────────────────────────────

/** Slide from right — use on panels, sidebars, drawers */
export const slideFromRight: Variants = {
  hidden: { opacity: 0, x: 24 },
  visible: { opacity: 1, x: 0, transition: springSmooth },
  exit: { opacity: 0, x: 24, transition: { duration: 0.15 } },
};

/** Slide from left — use on back-navigation panels */
export const slideFromLeft: Variants = {
  hidden: { opacity: 0, x: -24 },
  visible: { opacity: 1, x: 0, transition: springSmooth },
  exit: { opacity: 0, x: -24, transition: { duration: 0.15 } },
};

// ─── Stagger container ───────────────────────────────────────────────────────

/**
 * Wrap a list container with this to stagger children.
 * Children should use `fadeUp` or `listItem`.
 *
 * @example
 * <motion.ul variants={staggerContainer} initial="hidden" animate="visible">
 *   {items.map(i => <motion.li key={i.id} variants={listItem}>…</motion.li>)}
 * </motion.ul>
 */
export const staggerContainer: Variants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.04, delayChildren: 0.02 },
  },
};

/** Use as child of `staggerContainer` */
export const listItem: Variants = {
  hidden: { opacity: 0, y: 5 },
  visible: { opacity: 1, y: 0, transition: { ...easeSlide, duration: 0.18 } },
};

// ─── Toast / notification ────────────────────────────────────────────────────

/** Slides in from bottom-right — use on toast notifications */
export const toastSlide: Variants = {
  hidden: { opacity: 0, y: 12, scale: 0.96 },
  visible: { opacity: 1, y: 0, scale: 1, transition: springFast },
  exit: { opacity: 0, y: 8, scale: 0.97, transition: { duration: 0.15 } },
};
